import { useState } from 'react';
import { X, Loader2, CheckCircle2 } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import { useVertical } from '../../../../context/VerticalContext';
import { VRMLookup, type FinalVehicleData } from '../../../../components/vehicles/VRMLookup';
import { errorMessage } from '../../../../lib/errors';
import { vehicleColumnsFromLookup } from '../../../../lib/vehicle';
import { refreshMotHistoryWithin } from '../../../../services/vrmService';
import toast from 'react-hot-toast';

interface AddVehicleModalProps {
    customerId: string;
    onClose: () => void;
    onSuccess: () => void;
}

export default function AddVehicleModal({ customerId, onClose, onSuccess }: AddVehicleModalProps) {
    const [loading, setLoading] = useState(false);
    const { activeOrgId } = useVertical();
    const [confirmedVehicle, setConfirmedVehicle] = useState<FinalVehicleData | null>(null);

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!confirmedVehicle || !activeOrgId) return;
        
        setLoading(true);

        try {
            const { data: created, error } = await supabase
                .from('vehicles')
                .insert({
                    org_id: activeOrgId,
                    // Link the vehicle to the customer whose profile it's added from
                    // — without this the vehicle is orphaned (owner_id NULL) and
                    // won't show in booking/appointment/diagnostic vehicle pickers.
                    owner_id: customerId,
                    // Colour and fuel type used to be dropped here even though the
                    // lookup had them and the columns exist, which is why they had
                    // to be re-typed on the vehicle card afterwards.
                    ...vehicleColumnsFromLookup(confirmedVehicle),
                })
                .select('id')
                .single();

            if (error) throw error;

            // Pull the MOT history in before handing back, so the vehicle card
            // shows its MOT date, advisories and last odometer reading straight
            // away. Bounded — see refreshMotHistoryWithin — so a slow DVSA never
            // holds up a vehicle that is already saved.
            if (created?.id) await refreshMotHistoryWithin(created.id);

            onSuccess();
            onClose();
        } catch (err) {
            toast.error('Error adding vehicle: ' + errorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-scrim/80 backdrop-blur-sm animate-in fade-in">
            <div className="w-full max-w-lg bg-panel border border-line rounded-xl shadow-2xl p-6 relative">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-fg-4 hover:text-fg transition-colors"
                >
                    <X className="w-5 h-5" />
                </button>

                <h2 className="text-xl font-bold text-fg mb-6">Add New Vehicle</h2>

                {!confirmedVehicle ? (
                    <div className="space-y-4">
                        <p className="text-sm text-fg-3 mb-4">
                            Enter the vehicle registration to look up its details.
                        </p>
                        <VRMLookup 
                            onVehicleConfirmed={(data) => setConfirmedVehicle(data)} 
                        />
                    </div>
                ) : (
                    <div className="space-y-6 animate-in zoom-in-95 duration-200">
                        <div className="bg-success/5 border border-success/20 rounded-lg p-5">
                            <div className="flex items-center gap-3 mb-4">
                                <CheckCircle2 className="w-6 h-6 text-success" />
                                <h3 className="text-lg font-bold text-success">Vehicle Ready</h3>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-y-4 gap-x-4 text-sm mt-4">
                                <div>
                                    <span className="block text-fg-4 uppercase text-xs font-bold mb-1">VRM</span>
                                    <span className="text-fg font-mono font-bold bg-gold-fill/10 text-gold px-2 py-1 rounded select-all">{confirmedVehicle.vrm}</span>
                                </div>
                                <div>
                                    <span className="block text-fg-4 uppercase text-xs font-bold mb-1">Make & Model</span>
                                    <span className="text-fg font-medium">{confirmedVehicle.make} {confirmedVehicle.model}</span>
                                </div>
                                <div>
                                    <span className="block text-fg-4 uppercase text-xs font-bold mb-1">Year</span>
                                    <span className="text-fg">{confirmedVehicle.yearOfManufacture || 'N/A'}</span>
                                </div>
                                <div>
                                    <span className="block text-fg-4 uppercase text-xs font-bold mb-1">Colour</span>
                                    <span className="text-fg capitalize">{confirmedVehicle.colour || 'N/A'}</span>
                                </div>
                                <div>
                                    <span className="block text-fg-4 uppercase text-xs font-bold mb-1">Fuel</span>
                                    <span className="text-fg capitalize">
                                        {(confirmedVehicle.fuel_type ?? confirmedVehicle.fuelType) || 'N/A'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="pt-2 flex flex-col gap-3">
                            <button
                                onClick={handleSubmit}
                                disabled={loading}
                                className="w-full fd-btn-primary font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Saving Database Record...
                                    </>
                                ) : (
                                    'Save Vehicle to Customer'
                                )}
                            </button>
                            
                            <button
                                onClick={() => setConfirmedVehicle(null)}
                                disabled={loading}
                                className="w-full text-fg-3 hover:text-fg text-sm font-medium transition-colors py-2"
                            >
                                Lookup a different vehicle
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
